import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, Check, Info, Package, Ruler, ShoppingCart, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Product, ProductVariant } from '../../types';
import { useCart } from '../../context/CartContext';
import { SaveToPicksButton } from '../picks/SaveToPicksButton';
import { SizeFinder } from './SizeFinder';
import { ColourSwatch } from './ColourSwatch';
import { openExpressCheckout } from '../cart/CartDrawer';

const isSizeChartImage = (src: string) => /size-chart/.test(src);

export function BraceDetailModal({
  product,
  onClose,
  prefill,
}: {
  product: Product;
  onClose: () => void;
  prefill?: { color?: string; size?: string };
}) {
  const { addItem, openCart } = useCart();

  const uniqueColors = useMemo(() => {
    const seen = new Map<string, string>();
    for (const v of product.variants) {
      if (v.color && !seen.has(v.color)) seen.set(v.color, v.colorHex || '#1a1a1a');
    }
    return Array.from(seen.entries()).map(([color, hex]) => ({ color, hex }));
  }, [product.variants]);

  const [selectedColor, setSelectedColor] = useState<string | null>(prefill?.color || null);
  const [selectedSize, setSelectedSize] = useState<string | null>(prefill?.size || null);
  const [added, setAdded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showSizeGuide, setShowSizeGuide] = useState(false);

  // Auto-select when there is no real choice — the shopper never taps a
  // button with no alternative. Colour is otherwise REQUIRED: multi-colour
  // braces must not silently default (the old card added Black while
  // showing Beige).
  useEffect(() => {
    if (uniqueColors.length === 1 && !selectedColor) setSelectedColor(uniqueColors[0].color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sizeEntries = useMemo(() => {
    const source = selectedColor
      ? product.variants.filter((v) => v.color === selectedColor)
      : product.variants;
    const seen = new Map<string, ProductVariant>();
    for (const v of source) {
      if (v.size && !seen.has(v.size)) seen.set(v.size, v);
    }
    return Array.from(seen.entries());
  }, [product.variants, selectedColor]);

  useEffect(() => {
    if (sizeEntries.length === 1 && !selectedSize) setSelectedSize(sizeEntries[0][0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedVariant = useMemo(() => {
    if (!selectedColor || !selectedSize) return null;
    return product.variants.find((v) => v.color === selectedColor && v.size === selectedSize) || null;
  }, [product.variants, selectedColor, selectedSize]);

  const allSelected = !!selectedColor && (sizeEntries.length === 0 || !!selectedSize);

  // Gallery: colour-specific photos when a colour is chosen, else the shared
  // set. The size chart never rides in the gallery — it has the Size Guide.
  const galleryImages = useMemo(() => {
    const colorSet = selectedColor ? product.colorImages?.[selectedColor] : undefined;
    const base = (colorSet && colorSet.length > 0)
      ? colorSet
      : (product.images && product.images.length > 0)
      ? product.images
      : product.imageUrl ? [product.imageUrl] : [];
    return base.filter((src) => !isSizeChartImage(src));
  }, [product, selectedColor]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedColor]);

  const sizeChartImage = product.images?.find(isSizeChartImage);
  const sizingChart = product.sizingChart;
  const isPreorder = product.variants.some((v) => v.stock.status === 'preorder');

  // Auto-scroll to the next missing choice after a real interaction
  const userInteractedRef = useRef(false);
  const colorSectionRef = useRef<HTMLDivElement>(null);
  const sizeSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userInteractedRef.current) return;
    const next =
      !selectedColor && uniqueColors.length > 1 ? colorSectionRef.current
      : !selectedSize && sizeEntries.length > 0 ? sizeSectionRef.current
      : null;
    next?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, selectedSize]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleAddToCart = useCallback(() => {
    if (!selectedVariant) return;
    const enriched = {
      ...selectedVariant,
      variantDescription: [selectedColor, `Size ${selectedSize}`].filter(Boolean).join(', '),
    };
    addItem(product, enriched);
    setAdded(true);
    toast.success(`${product.name} (${enriched.variantDescription}) added to cart`, { icon: '\u{1F9BE}' });
    setTimeout(() => setAdded(false), 2000);
  }, [addItem, product, selectedVariant, selectedColor, selectedSize]);

  const pickData = useMemo(() => {
    const selections = {
      color: selectedColor || undefined,
      size: selectedSize || undefined,
    };
    if (!selectedVariant) return { variant: null as ProductVariant | null, selections };
    return {
      variant: {
        ...selectedVariant,
        variantDescription: [selectedColor, `Size ${selectedSize}`].filter(Boolean).join(', '),
      } as ProductVariant,
      selections,
    };
  }, [selectedVariant, selectedColor, selectedSize]);

  const chartHasNonCmUnits = sizingChart?.rows.some((r) => r.unit && r.unit !== 'cm');

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40, scale: 0.97 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto scrollbar-thin shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-[#7B68CC] uppercase tracking-wide">{product.brand}</span>
              {product.series && (
                <span className="text-xs font-bold text-gray-400">Series {product.series}</span>
              )}
              {product.popular && (
                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-[#7B68CC] to-[#a594e6] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                  <Sparkles size={9} className="fill-white" />
                  Popular
                </span>
              )}
            </div>
            <h2 className="font-bold text-ndms-navy text-lg leading-tight">{product.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-5">
          {/* Availability notice */}
          {isPreorder && (
            <div className="flex items-start gap-2.5 bg-[#f5f3ff] border border-[#7B68CC]/20 rounded-xl px-3.5 py-2.5">
              <Package size={15} className="text-[#7B68CC] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-600 leading-snug">
                <span className="font-bold text-ndms-navy">Typically in stock.</span> Reserve it here and pick it up
                in store — if it's not in stock, it typically arrives in about 1–2 days. No payment now; you pay in store.
              </p>
            </div>
          )}

          {/* Image gallery */}
          {galleryImages.length > 0 && (
            <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#f5f3ff] to-[#ede9fe]">
              <img
                src={galleryImages[Math.min(currentImageIndex, galleryImages.length - 1)]}
                alt={`${product.name} - ${currentImageIndex + 1} of ${galleryImages.length}`}
                className="w-full max-h-[60vh] object-contain bg-white"
              />
              {galleryImages.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex((i) => (i + 1) % galleryImages.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow"
                    aria-label="Next image"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {galleryImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIndex(i)}
                        aria-label={`Image ${i + 1}`}
                        className={`w-2 h-2 rounded-full transition-all ${
                          i === Math.min(currentImageIndex, galleryImages.length - 1) ? 'bg-white scale-125 shadow' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Product details */}
          <div className="space-y-3">
            <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {product.material && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Material</p>
                  <p className="text-sm text-gray-700">{product.material}</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-xl px-3 py-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Body Area</p>
                <p className="text-sm text-gray-700 capitalize">{product.subcategory}</p>
              </div>
              {product.latexFree === true && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Latex</p>
                  <p className="text-sm text-gray-700">Latex-free</p>
                </div>
              )}
              {product.latexFree === false && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Latex</p>
                  <p className="text-sm text-amber-800">Contains latex — check for allergies</p>
                </div>
              )}
              {sizeEntries.length > 0 && (
                <div className="bg-gray-50 rounded-xl px-3 py-2">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Sizes</p>
                  <p className="text-sm text-gray-700">{sizeEntries.map(([s]) => s).join(', ')}</p>
                </div>
              )}
            </div>

            {product.indications && product.indications.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-ndms-navy mb-1.5 flex items-center gap-1">
                  <Info size={12} /> Helps with
                </p>
                <ul className="space-y-1">
                  {product.indications.map((ind) => (
                    <li key={ind} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={14} className="text-[#7B68CC] mt-0.5 flex-shrink-0" />
                      {ind}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.features.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-ndms-navy mb-1.5 flex items-center gap-1">
                  <Info size={12} /> Features
                </p>
                <ul className="space-y-1">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Colour selector — required (auto-selected when single) */}
          {uniqueColors.length > 1 && (
            <div ref={colorSectionRef}>
              <p className="text-xs font-semibold text-ndms-navy mb-3">
                Choose Colour ({uniqueColors.length} available)
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {uniqueColors.map(({ color, hex }) => (
                  <div key={color} className="flex flex-col items-center gap-1">
                    <ColourSwatch
                      color={color}
                      colorHex={hex}
                      selected={selectedColor === color}
                      onClick={() => { userInteractedRef.current = true; setSelectedColor(color); setSelectedSize(null); }}
                      size="lg"
                    />
                    <span className="text-[9px] text-gray-400 text-center max-w-[50px] leading-tight truncate">
                      {color}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Find My Size — measurement-based recommender */}
          {sizeEntries.length > 0 && sizingChart && (
            <SizeFinder
              sizingChart={sizingChart}
              availableSizes={sizeEntries.map(([s]) => s)}
              selectedSize={selectedSize}
              onSelectSize={(s) => { userInteractedRef.current = true; setSelectedSize(s); }}
              showMeasuringGuideLink={false}
            />
          )}

          {/* Size selector + Size Guide */}
          {sizeEntries.length > 0 && (
            <div ref={sizeSectionRef}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-ndms-navy">
                  Choose Size ({sizeEntries.length} available)
                </p>
                {(sizingChart || sizeChartImage) && (
                  <button
                    onClick={() => setShowSizeGuide(true)}
                    className="text-xs text-[#7B68CC] font-semibold hover:underline flex items-center gap-1"
                  >
                    <Ruler size={12} /> Size Guide
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sizeEntries.map(([size, variant]) => {
                  const outOfStock = variant.stock.status === 'out_of_stock';
                  return (
                    <button
                      key={size}
                      onClick={() => { if (!outOfStock) { userInteractedRef.current = true; setSelectedSize(size); } }}
                      disabled={outOfStock}
                      className={`min-w-[44px] px-2.5 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        selectedSize === size
                          ? 'bg-ndms-navy text-white border-ndms-navy'
                          : outOfStock
                          ? 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed line-through'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-ndms-navy hover:text-ndms-navy'
                      }`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer: Save to Picks + Add to Cart + Order Now */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4">
          <div className="flex items-stretch gap-2">
            <SaveToPicksButton
              product={product}
              variant={pickData.variant}
              selections={pickData.selections}
              size="lg"
              lineName={product.name}
            />
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => allSelected && handleAddToCart()}
              disabled={!allSelected}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-sm sm:text-base transition-all duration-300 ${
                added
                  ? 'bg-green-500 text-white'
                  : allSelected
                  ? 'bg-ndms-navy text-white hover:bg-[#7B68CC] shadow-lg hover:shadow-xl'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <AnimatePresence mode="wait">
                {added ? (
                  <motion.span key="added" initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-2">
                    <Check size={18} /> Added!
                  </motion.span>
                ) : (
                  <motion.span key="add" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2">
                    <ShoppingCart size={18} />
                    {allSelected
                      ? 'Add to Cart'
                      : !selectedColor ? 'Select a Colour'
                      : 'Select a Size'}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
            {allSelected && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => {
                  handleAddToCart();
                  onClose();
                  openCart();
                  openExpressCheckout();
                }}
                className="flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-2xl font-bold text-sm sm:text-base border-2 border-ndms-navy text-ndms-navy hover:bg-ndms-navy hover:text-white transition-all"
              >
                Order Now →
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Size Guide Modal */}
      <AnimatePresence>
        {showSizeGuide && (sizingChart || sizeChartImage) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            onClick={(e) => { e.stopPropagation(); setShowSizeGuide(false); }}
          >
            <div className="absolute inset-0 bg-black/60" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h3 className="font-bold text-ndms-navy text-base">Size Guide</h3>
                  <p className="text-xs text-gray-500">{product.name}</p>
                </div>
                <button onClick={() => setShowSizeGuide(false)} className="p-2 hover:bg-gray-100 rounded-xl" aria-label="Close size guide">
                  <X size={18} />
                </button>
              </div>
              <div className="px-5 py-4 space-y-4">
                {sizingChart && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse min-w-[400px]">
                      <thead>
                        <tr className="bg-[#7B68CC]/10">
                          <th className="px-3 py-2 text-left font-semibold text-ndms-navy border border-gray-200 sticky left-0 bg-[#7B68CC]/10">Measurement</th>
                          {sizingChart.columns.map((col) => (
                            <th key={col} className="px-2 py-2 text-center font-bold text-[#7B68CC] border border-gray-200 whitespace-nowrap">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sizingChart.rows.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-3 py-2 font-medium text-gray-700 border border-gray-200 sticky left-0 bg-inherit whitespace-nowrap">{row.measurement}</td>
                            {row.values.map((val, j) => (
                              <td key={j} className="px-2 py-2 text-center text-gray-600 border border-gray-200 whitespace-nowrap">{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 mt-2">
                      {chartHasNonCmUnits ? 'Measurements in cm unless noted.' : 'All measurements in cm.'}
                    </p>
                  </div>
                )}
                {sizeChartImage && (
                  <div>
                    {sizingChart && (
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Printed chart</p>
                    )}
                    <img src={sizeChartImage} alt={`${product.name} size chart`} className="w-full rounded-xl border border-gray-100" />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
